
import React, { useState, useEffect, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import toast from "react-hot-toast";
import BidSubmitConfirmationModal from "../Modal/supplier_app/BidSubmitConfirmationModal";

// 
// Global styles: DataTable + badges + animations
// 
const COMPONENT_STYLES = `
  /*  DataTable  */
  .bsp-dt {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    font-size: 14px;
  }
  .bsp-dt thead tr {
    background: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
  }
  .bsp-dt thead th {
    padding: 13px 16px;
    text-align: left;
    font-size: 11.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #6b7280;
    white-space: nowrap;
  }
  .bsp-dt thead th.c { text-align: center; }
  .bsp-dt thead th.r { text-align: right; }

  .bsp-dt tbody tr {
    border-bottom: 1px solid #f3f4f6;
    transition: background 0.1s ease;
  }
  .bsp-dt tbody tr:last-child { border-bottom: none; }
  .bsp-dt tbody tr:hover      { background: #f9fafb !important; }
  .bsp-dt tbody td {
    padding: 13px 16px;
    vertical-align: middle;
    font-size: 14px;
    color: #1f2937;
  }
  .bsp-dt tbody td.c { text-align: center; }
  .bsp-dt tbody td.r { text-align: right; }

  /* Row status accents */
  .bsp-dt tbody tr.row-active  { border-left: 3px solid #10b981; }
  .bsp-dt tbody tr.row-pending { border-left: 3px solid #d1d5db; }
  .bsp-dt tbody tr.row-closed  { border-left: 3px solid #fca5a5; }
  .bsp-dt tbody tr.row-quote   { border-left: 3px solid #818cf8; }
  .bsp-dt tbody tr.row-ended   { border-left: 3px solid #e5e7eb; }

  /* ── Pill badges  */
  .pill {
    display: inline-flex; align-items: center; gap: 4px;
    border-radius: 20px; padding: 3px 11px;
    font-size: 12px; font-weight: 700; white-space: nowrap; line-height: 1.5;
  }
  .pill-green  { background: #f0fdf4; color: #065f46; border: 1px solid #a7f3d0; }
  .pill-blue   { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
  .pill-gray   { background: #f3f4f6; color: #374151; border: 1px solid #d1d5db; }
  .pill-indigo { background: #eef2ff; color: #3730a3; border: 1px solid #c7d2fe; }
  .pill-red    { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
  .pill-purple { background: #f5f3ff; color: #4c1d95; border: 1px solid #ddd6fe; }
  .pill-teal   { background: #f0fdfa; color: #0f766e; border: 1px solid #99f6e4; }
  .pill-amber  { background: #fffbeb; color: #78350f; border: 1px solid #fde68a; }
  .pill-slate  { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; }

  /* ── Rank badges  */
  .rank-l1 { background: #f0fdf4; color: #059669; border: 1px solid #86efac; }
  .rank-ln { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; }

  /* ── Animations  */
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.6; transform: scale(0.85); }
  }

  /* ── Section card wrap  */
  .bsp-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
    margin-bottom: 16px;
  }
  .bsp-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    cursor: pointer;
    user-select: none;
  }
  .bsp-card-head-title {
    font-weight: 700;
    font-size: 13px;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* ── Info banners  */
  .bsp-banner {
    padding: 10px 18px;
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    border-bottom: 1px solid transparent;
  }
  .bsp-banner.banner-blue   { background: #f0f7ff; border-color: #bfdbfe; color: #1e40af; }
  .bsp-banner.banner-green  { background: #f0fdf4; border-color: #a7f3d0; color: #065f46; }
  .bsp-banner.banner-slate  { background: #f8fafc; border-color: #e2e8f0; color: #475569; }
  .bsp-banner.banner-indigo { background: #eef2ff; border-color: #c7d2fe; color: #3730a3; }
  .bsp-banner.banner-purple { background: #f5f3ff; border-color: #ddd6fe; color: #4c1d95; }

  /* ── Projected total bar  */
  .bsp-total-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 18px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 14px;
  }
  .bsp-total-label { font-size: 14px; font-weight: 600; color: #6b7280; }
  .bsp-total-value { font-size: 17px; font-weight: 800; color: #0f766e; }
  .bsp-total-value.quote { color: #3730a3; }

  /* ── Bid input  */
  .bsp-input {
    width: 110px;
    padding: 7px 9px;
    border: 1.5px solid #d1d5db;
    border-radius: 6px;
    font-size: 13.5px;
    outline: none;
    background: #fff;
    color: #111827;
    transition: border-color 0.15s;
  }
  .bsp-input:focus { border-color: #6366f1; }
  .bsp-input.input-quote { border-color: #a5b4fc; background: #fafafe; }
  .bsp-input.input-active { border-color: #d1d5db; }
  .bsp-input:disabled { background: #f9fafb; color: #9ca3af; cursor: not-allowed; border-color: #e5e7eb; }

  /* ── Submit buttons  */
  .bsp-btn {
    padding: 7px 18px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    border: none;
    white-space: nowrap;
    transition: background 0.15s, box-shadow 0.15s;
    cursor: pointer;
    letter-spacing: 0.01em;
  }
  .bsp-btn:disabled {
    background: #e5e7eb !important;
    color: #9ca3af !important;
    cursor: not-allowed;
    box-shadow: none !important;
  }
  /* All active submit buttons → unified muted teal */
  .bsp-btn.btn-indigo { background: #6aacb0; color: #fff; }
  .bsp-btn.btn-indigo:hover:not(:disabled) { background: #5a9ca0; box-shadow: 0 2px 8px rgba(106,172,176,0.35); }
  .bsp-btn.btn-teal   { background: #6aacb0; color: #fff; }
  .bsp-btn.btn-teal:hover:not(:disabled)   { background: #5a9ca0; box-shadow: 0 2px 8px rgba(106,172,176,0.35); }
  .bsp-btn.btn-blue   { background: #6aacb0; color: #fff; }
  .bsp-btn.btn-blue:hover:not(:disabled)   { background: #5a9ca0; box-shadow: 0 2px 8px rgba(106,172,176,0.35); }
  .bsp-btn.btn-closed { background: #f3f4f6; color: #9ca3af; }
`;

// Shared table header / cell constants (used where className isn't possible)
const thS = {
  padding: "13px 16px", textAlign: "left", color: "#6b7280",
  fontWeight: 700, fontSize: "11.5px", textTransform: "uppercase",
  letterSpacing: "0.07em", background: "#f9fafb",
  borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap",
};
const tdS = { padding: "13px 16px", verticalAlign: "middle", fontSize: "14px", color: "#1f2937" };
//eslint-disable-next-line
const th  = { ...thS };
//eslint-disable-next-line
const td  = { ...tdS };

// Component
const BidSubmitPanel = ({
  auctionItems = [],
  mbdType = "anyAmount",
  mbdValue = 0,
  startPrice = false,
  itemPrices = {},
  currency = "",
  fractions = 2,
  emitPlaceBid,
  lastBidStatus = null,
  auctionStatus = "active",
  isConnected = false,
  supplierCode = "",
  supplierName = "",
  auctionId = "",
  liveBidRanking = [],
  auctionMode = "reverse",
  bidConfigType = "parallel",
  bidTimings = {},
  bidType = "perUnit",
  itemQuantities = {},
  itemLeaderboard = [],
  userRole = "supplier",
  serverRemainingSeconds = null,
  itemMbd = {},
  itemReservePrice = {},
  supplierInitialPrices = {},
  supplierList = [],
  firstQuoteBy = "Supplier",
  firstQuote = false,
  bidAgainst = "leadingPrice",
  isAuctionClosed = false,
}) => {
  const isBuyer   = userRole === "buyer";
  const decimals  = parseInt(fractions) || 2;
  const mbd       = parseFloat(mbdValue) || 0;
  const mode      = (auctionMode || "").toLowerCase();
  const isForward = mode === "forward";
  const isReverse = mode === "reverse";
  const isEnded   = auctionStatus === "ended";
  const auctionIsActuallyEnded = isAuctionClosed || (isEnded && (serverRemainingSeconds === null || serverRemainingSeconds <= 0));
  const canBidGlobal = !isBuyer && isConnected;
  const canBid    = bidConfigType === "parallel"
    ? (canBidGlobal && !auctionIsActuallyEnded)
    : canBidGlobal;
// eslint-disable-next-line
  const supplierHasQuoteForItem = (itemKey) => {
    const sKey = String(itemKey);
    const rankings = getItemRankings(sKey);
    return rankings.some(r => String(r.supplierCode) === String(supplierCode));
  };

  const getInitialRankingsForItem = (itemKey) => {
    if (firstQuoteBy !== "Buyer" || !supplierInitialPrices || Object.keys(supplierInitialPrices).length === 0) {
      return [];
    }
    const pricesArray = [];
    Object.entries(supplierInitialPrices).forEach(([supplierId, itemPricesMap]) => {
      const price = parseFloat(itemPricesMap?.[itemKey]);
      if (!isNaN(price) && price >= 0) {
        const supplier = supplierList.find(s => String(s.AssociateNumber) === String(supplierId));
        pricesArray.push({
          supplierId,
          supplierName: supplier?.organisationName || supplierId,
          supplierCode: supplier?.AssociateNumber || supplierId,
          bidAmount: price,
        });
      }
    });
    if (pricesArray.length === 0) return [];
    if (isReverse) {
      pricesArray.sort((a, b) => a.bidAmount - b.bidAmount);
    } else {
      pricesArray.sort((a, b) => b.bidAmount - a.bidAmount);
    }
    return pricesArray.map((entry, idx) => ({
      rank: idx + 1,
      supplierCode: entry.supplierCode,
      supplierName: entry.supplierName,
      bidAmount: entry.bidAmount,
      isInitialPrice: true,
      bidTime: null,
    }));
  };

  const getItemRankings = (itemKey) => {
    const sKey = String(itemKey);
    let itemRankEntry = itemLeaderboard.find(lb =>
      lb.local_id !== undefined && lb.local_id !== null && String(lb.local_id) !== "" &&
      String(lb.local_id) === sKey
    );
    if (!itemRankEntry) {
      itemRankEntry = itemLeaderboard.find(lb =>
        lb.local_id === undefined || lb.local_id === null || String(lb.local_id) === ""
          ? (lb.itemKey !== undefined && lb.itemKey !== null && String(lb.itemKey) === sKey)
          : false
      );
    }
    if (!itemRankEntry) {
      itemRankEntry = itemLeaderboard.find(lb =>
        (lb.local_id === undefined || lb.local_id === null || String(lb.local_id) === "") &&
        (lb.itemKey  === undefined || lb.itemKey  === null || String(lb.itemKey)  === "") &&
        lb.itemCode !== undefined && lb.itemCode !== null && String(lb.itemCode) === sKey
      );
    }
    if (itemRankEntry?.rankings && itemRankEntry.rankings.length > 0) {
      return [...itemRankEntry.rankings].sort((a, b) => (a.rank || 999) - (b.rank || 999));
    }
    return getInitialRankingsForItem(sKey);
  };

  const isInitialQuoteMode = !isBuyer &&
    firstQuote === true &&
    firstQuoteBy === "Supplier" &&
    !auctionIsActuallyEnded &&
    auctionItems.some(item => {
      const key = String(item.local_id ?? item.itemCode ?? "");
      return !supplierHasQuoteForItem(key);
    });

  const getOwnLastBidForItem = (itemKey) => {
    const sKey = String(itemKey);
    for (const lb of itemLeaderboard) {
      const lbKey = String(lb.local_id ?? lb.itemKey ?? lb.itemCode ?? "");
      if (lbKey !== sKey) continue;
      if (!lb.rankings) continue;
      const myRank = lb.rankings.find(r => String(r.supplierCode) === String(supplierCode));
      if (myRank) return parseFloat(myRank.bidAmount);
    }
    return null;
  };

  const getActiveItemIds = () => {
    if (bidConfigType === "parallel") {
      return new Set(auctionItems.map(i =>
        (i.local_id !== undefined && i.local_id !== null)
          ? String(i.local_id)
          : (i.itemCode !== undefined && i.itemCode !== null ? String(i.itemCode) : String(i._id))
      ));
    }
    const now = new Date();
    const activeIds = new Set();
    for (const item of auctionItems) {
      const key    = (item.local_id !== undefined && item.local_id !== null) ? String(item.local_id) : (item.itemCode !== undefined && item.itemCode !== null ? String(item.itemCode) : String(item._id));
      const timing = bidTimings?.[key];
      if (timing) {
        const start = new Date(timing.startTime);
        const end   = new Date(timing.endTime);
        if (now >= start && now < end) activeIds.add(key);
      }
    }
    return activeIds;
  };

  //eslint-disable-next-line
  const [activeItemIds,   setActiveItemIds]   = useState(() => getActiveItemIds());
  const [bidAmounts,      setBidAmounts]       = useState({});
  const [remarks,         setRemarks]          = useState("");
  const [isSubmitting,    setIsSubmitting]     = useState({});
  const [isCardVisible,   setIsCardVisible]    = useState(true);
  const [showItemRanking, setShowItemRanking]  = useState(true);
  const [showLotTimings,  setShowLotTimings]   = useState(true);
  const [bidConfirmModal, setBidConfirmModal] = useState({ show: false, itemKey: null, item: null, amount: 0 });

  useEffect(() => {
    if (bidConfigType === "parallel") return;
    const iv = setInterval(() => setActiveItemIds(getActiveItemIds()), 5000);
    return () => clearInterval(iv);
    //eslint-disable-next-line
  }, [bidConfigType, bidTimings, auctionItems]);

  useEffect(() => {
    if (auctionItems.length > 0) {
      const initial = {};
      auctionItems.forEach(item => {
        const key = item.local_id ?? item.itemCode ?? String(item._id);
        initial[key] = "";
      });
      setBidAmounts(initial);
    }
  }, [auctionItems]);

  useEffect(() => {
    if (lastBidStatus) {
      setIsSubmitting({});
      if (lastBidStatus.status === "accepted") {
        setBidAmounts(prev => {
          const r = {};
          Object.keys(prev).forEach(k => r[k] = "");
          return r;
        });
        setRemarks("");
      }
    }
  }, [lastBidStatus]);

  const currentBidValue = liveBidRanking.length > 0
    ? isReverse
      ? Math.min(...liveBidRanking.map(r => parseFloat(r.totalBid) || Infinity))
      : Math.max(...liveBidRanking.map(r => parseFloat(r.totalBid) || -Infinity))
    : null;

  //eslint-disable-next-line
  const myRank = !isBuyer
    ? liveBidRanking.find(r => String(r.supplierCode) === String(supplierCode))
    : null;
// eslint-disable-next-line
  const projectedTotal = auctionItems.reduce((sum, item) => {
    const key = item.local_id ?? item.itemCode ?? String(item._id);
    const val = parseFloat(bidAmounts[key]) || 0;
    const qty = parseFloat(item.quantity) || 1;
    return sum + (val * qty);
  }, 0);

  const getItemStartPrice = (item) => {
    const key = item.local_id ?? item.itemCode;
    const sp  = itemPrices?.[key];
    return sp ? parseFloat(sp) : null;
  };

  const totalStartPrice = auctionItems.reduce((sum, item) => {
    const sp = getItemStartPrice(item);
    return sum + (sp ? sp * (parseFloat(item.quantity) || 1) : 0);
  }, 0);

  const referencePrice = currentBidValue !== null
    ? currentBidValue
    : (startPrice && totalStartPrice > 0 ? totalStartPrice : null);

  const fmt = (val) => {
    if (val == null || val === "") return "--";
    return Number(val).toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return "--";
    try {
      return new Date(isoString).toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      });
    } catch { return "--"; }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "--";
    try {
      return new Date(isoString).toLocaleString("en-IN", {
        hour12: true, hour: "2-digit", minute: "2-digit",
        day: "2-digit", month: "short",
      });
    } catch { return "--"; }
  };

  const handleAmountChange = (key, value) => {
    if (/^\d*\.?\d*$/.test(value) || value === "") {
      setBidAmounts(prev => ({ ...prev, [key]: value }));
    }
  };

  const validateItemBid = (key, val, item) => {
    const itemKey = (item.local_id !== undefined && item.local_id !== null)
      ? String(item.local_id)
      : (item.itemCode !== undefined && item.itemCode !== null)
        ? String(item.itemCode)
        : String(item._id || key);

    if (firstQuote && firstQuoteBy === "Supplier") {
      const hasQuoted = supplierHasQuoteForItem(itemKey);
      if (!hasQuoted) {
        if (val <= 0) {
          return { valid: false, message: `${item?.itemCode || key}: Please enter a valid price greater than 0` };
        }
        return { valid: true };
      }
    }

    const perItemMbd = parseFloat(itemMbd?.[itemKey]) || mbd;
    const itemSP     = parseFloat(
      itemPrices?.[itemKey] || itemPrices?.[item?.local_id] || itemPrices?.[item?.itemCode]
    );

    if (startPrice && !isNaN(itemSP) && itemSP > 0) {
      const ownLastBid = getOwnLastBidForItem(itemKey);
      if (ownLastBid === null) {
        // First bid - apply MBD validation against start price
        if (mbdType === "anyAmount" || perItemMbd <= 0) {
          if (isReverse && val >= itemSP)
            return { valid: false, message: `${item?.itemCode || key}: First bid must be lower than start price (${currency} ${fmt(itemSP)})` };
          if (isForward && val <= itemSP)
            return { valid: false, message: `${item?.itemCode || key}: First bid must be higher than start price (${currency} ${fmt(itemSP)})` };
          return { valid: true };
        }
        // Apply MBD validation for first bid
        if (isReverse) {
          if (val >= itemSP)
            return { valid: false, message: `${item?.itemCode || key}: First bid must be lower than start price (${currency} ${fmt(itemSP)})` };
          if (mbdType === "atLeastMbd" && val > itemSP - perItemMbd)
            return { valid: false, message: `${item?.itemCode || key}: First bid must be at least ${currency} ${fmt(perItemMbd)} lower than start price. Max: ${currency} ${fmt(itemSP - perItemMbd)}` };
          if (mbdType === "multiplesMbd") {
            const diff = parseFloat((itemSP - val).toFixed(decimals));
            if (diff <= 0 || Math.round((diff % perItemMbd) * 1e9) / 1e9 !== 0)
              return { valid: false, message: `${item?.itemCode || key}: First bid must be a multiple of ${currency} ${fmt(perItemMbd)} below start price (${currency} ${fmt(itemSP)})` };
          }
        } else if (isForward) {
          if (val <= itemSP)
            return { valid: false, message: `${item?.itemCode || key}: First bid must be higher than start price (${currency} ${fmt(itemSP)})` };
          if (mbdType === "atLeastMbd" && val < itemSP + perItemMbd)
            return { valid: false, message: `${item?.itemCode || key}: First bid must be at least ${currency} ${fmt(perItemMbd)} higher than start price. Min: ${currency} ${fmt(itemSP + perItemMbd)}` };
          if (mbdType === "multiplesMbd") {
            const diff = parseFloat((val - itemSP).toFixed(decimals));
            if (diff <= 0 || Math.round((diff % perItemMbd) * 1e9) / 1e9 !== 0)
              return { valid: false, message: `${item?.itemCode || key}: First bid must be a multiple of ${currency} ${fmt(perItemMbd)} above start price (${currency} ${fmt(itemSP)})` };
          }
        }
        return { valid: true };
      }
    }

    let resolvedReference = null;

    if (bidAgainst === "ownPrice") {
      resolvedReference = getOwnLastBidForItem(itemKey);
      if (resolvedReference === null) return { valid: true };
    } else {
      const rankings    = getItemRankings(itemKey);
      resolvedReference = rankings.length > 0 ? parseFloat(rankings[0].bidAmount) : null;
      if (resolvedReference === null && !isNaN(itemSP) && itemSP > 0) {
        resolvedReference = itemSP;
      }
      if (resolvedReference === null) return { valid: true };
    }

    const modeLabel = bidAgainst === "ownPrice"
      ? `your own last bid (${currency} ${fmt(resolvedReference)})`
      : `current leading price (${currency} ${fmt(resolvedReference)})`;

    if (mbdType === "anyAmount" || perItemMbd <= 0) {
      if (isReverse && val >= resolvedReference)
        return { valid: false, message: `${item?.itemCode || key}: Bid must be lower than ${modeLabel}` };
      if (isForward && val <= resolvedReference)
        return { valid: false, message: `${item?.itemCode || key}: Bid must be higher than ${modeLabel}` };
      return { valid: true };
    }

    if (isReverse) {
      if (val >= resolvedReference)
        return { valid: false, message: `${item?.itemCode || key}: Bid must be lower than ${modeLabel}` };
      if (mbdType === "atLeastMbd" && val > resolvedReference - perItemMbd)
        return { valid: false, message: `${item?.itemCode || key}: Must be at least ${currency} ${fmt(perItemMbd)} lower than ${modeLabel}. Max: ${currency} ${fmt(resolvedReference - perItemMbd)}` };
      if (mbdType === "multiplesMbd") {
        const diff = parseFloat((resolvedReference - val).toFixed(decimals));
        if (diff <= 0 || Math.round((diff % perItemMbd) * 1e9) / 1e9 !== 0)
          return { valid: false, message: `${item?.itemCode || key}: Must be a multiple of ${currency} ${fmt(perItemMbd)} below ${modeLabel}` };
      }
    } else if (isForward) {
      if (val <= resolvedReference)
        return { valid: false, message: `${item?.itemCode || key}: Bid must be higher than ${modeLabel}` };
      if (mbdType === "atLeastMbd" && val < resolvedReference + perItemMbd)
        return { valid: false, message: `${item?.itemCode || key}: Must be at least ${currency} ${fmt(perItemMbd)} higher than ${modeLabel}. Min: ${currency} ${fmt(resolvedReference + perItemMbd)}` };
      if (mbdType === "multiplesMbd") {
        const diff = parseFloat((val - resolvedReference).toFixed(decimals));
        if (diff <= 0 || Math.round((diff % perItemMbd) * 1e9) / 1e9 !== 0)
          return { valid: false, message: `${item?.itemCode || key}: Must be a multiple of ${currency} ${fmt(perItemMbd)} above ${modeLabel}` };
      }
    }

    return { valid: true };
  };

  const handleSingleItemSubmit = (key, item) => {
    if (!canBid) {
      toast.error(isEnded ? "Auction has ended." : "Not connected to auction server.", { position: "top-right" });
      return;
    }

    const itemAlreadyQuoted = firstQuote && firstQuoteBy === "Supplier" && supplierHasQuoteForItem(key);
    if (bidConfigType !== "parallel" && itemAlreadyQuoted) {
      if (getLotStatus(key) !== "active") {
        const timing  = bidTimings?.[key];
        const now     = new Date();
        const isClose = timing && now >= new Date(timing.endTime);
        toast.error(
          isClose ? `Bidding for ${item.itemCode || key} has closed.` : `Bidding for ${item.itemCode || key} is not open yet.`,
          { position: "top-right" }
        );
        return;
      }
    }

    const val = parseFloat(bidAmounts[key]);
    if (!bidAmounts[key] || isNaN(val) || val <= 0) {
      toast.error(`Enter a valid price for: ${item.itemCode || item.itemNo || key}`, { position: "top-right" });
      return;
    }

    const check = validateItemBid(key, val, item);
    if (!check.valid) {
      toast.error(check.message, { position: "top-right", duration: 5000 });
      return;
    }

    setBidConfirmModal({ show: true, itemKey: key, item, amount: val });
  };

  const handleConfirmBid = useCallback(() => {
    const { itemKey, item, amount: val } = bidConfirmModal;
    if (!itemKey || !item) return;

    const qty = parseFloat(item.quantity) || 1;
    setIsSubmitting(prev => ({ ...prev, [itemKey]: true }));

    const sent = emitPlaceBid?.({
      auctionId,
      supplierCode,
      supplierName,
      items: [{
        local_id:     item.local_id ?? itemKey,
        itemCode:     item.itemCode ?? itemKey,
        itemDesc:     item.itemDescription || item.itemNo || "",
        bidAmount:    parseFloat(val.toFixed(decimals)),
        uom:          item.uom || "",
        quantity:     qty,
        currency,
        lotNumber:    bidTimings?.[itemKey]?.lotNumber    ?? null,
        serialNumber: bidTimings?.[itemKey]?.serialNumber ?? null,
      }],
      totalBid:      parseFloat((val * qty).toFixed(decimals)),
      remarks,
      bidTime:       new Date().toISOString(),
      singleItemBid: true,
      itemKey:       itemKey,
      isInitialQuote: firstQuote && firstQuoteBy === "Supplier" && !supplierHasQuoteForItem(itemKey),
    });

    if (!sent) setIsSubmitting(prev => ({ ...prev, [itemKey]: false }));
    setBidConfirmModal({ show: false, itemKey: null, item: null, amount: 0 });
  }, [bidConfirmModal, emitPlaceBid, auctionId, supplierCode, supplierName, currency, decimals, remarks, bidTimings, firstQuote, firstQuoteBy, supplierHasQuoteForItem]);

  const getLotStatus = (key) => {
    if (bidConfigType === "parallel") return "active";
    const timing = bidTimings?.[key];
    if (!timing) return "active";
    const now = new Date();
    if (now < new Date(timing.startTime)) return "pending";
    if (now >= new Date(timing.endTime))  return "closed";
    return "active";
  };

  const getLotStatusMeta = (status) => ({
    active:  { bg: "#f0fdf4", border: "#a7f3d0", label: "🟢 Live",       color: "#065f46" },
    pending: { bg: "#f8fafc", border: "#cbd5e1", label: "⏳ Opens Later", color: "#94a3b8" },
    closed:  { bg: "#fef2f2", border: "#fecaca", label: "🔴 Closed",      color: "#991b1b" },
  }[status] || {});

  const getRankBadge = (rank) => ({
    1: { emoji: "🥇", bg: "#f0fdf4", color: "#065f46", border: "#86efac" },
    2: { emoji: "🥈", bg: "#f3f4f6", color: "#374151", border: "#d1d5db" },
    3: { emoji: "🥉", bg: "#fff7ed", color: "#7c2d12", border: "#fed7aa" },
  }[rank] || { emoji: "🏅", bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" });

  const getLotTimingRows = () => {
    if (bidConfigType === "parallel" || !auctionItems.length) return [];
    if (bidConfigType === "staggered") {
      return auctionItems.map(item => {
        const key    = (item.local_id !== undefined && item.local_id !== null) ? String(item.local_id) : (item.itemCode !== undefined && item.itemCode !== null ? String(item.itemCode) : String(item._id));
        const timing = bidTimings?.[key];
        return { lotNumber: item.itemCode || key, items: [item], startTime: timing?.startTime, endTime: timing?.endTime };
      }).sort((a, b) => new Date(a.endTime || 0) - new Date(b.endTime || 0));
    }
    const lotMap = {};
    auctionItems.forEach(item => {
      const key    = (item.local_id !== undefined && item.local_id !== null) ? String(item.local_id) : (item.itemCode !== undefined && item.itemCode !== null ? String(item.itemCode) : String(item._id));
      const timing = bidTimings?.[key];
      const lotNo  = timing?.lotNumber ?? "--";
      const lotKey = String(lotNo);
      if (!lotMap[lotKey]) {
        lotMap[lotKey] = { lotNumber: lotNo, items: [], startTime: timing?.startTime, endTime: timing?.endTime };
      }
      lotMap[lotKey].items.push(item);
    });
    return Object.values(lotMap).sort((a, b) => {
      const aNum = Number(a.lotNumber), bNum = Number(b.lotNumber);
      if (isNaN(aNum) || isNaN(bNum)) return String(a.lotNumber).localeCompare(String(b.lotNumber));
      return aNum - bNum;
    });
  };
  const lotTimingRows = getLotTimingRows();

  const getActiveLot = () => {
    if (bidConfigType !== "serial") return null;
    const now = new Date();
    for (const row of lotTimingRows) {
      if (row.startTime && row.endTime) {
        if (now >= new Date(row.startTime) && now < new Date(row.endTime))
          return String(row.lotNumber);
      }
    }
    return null;
  };
  const activeLot = getActiveLot();

  const getMbdHint = () => {
    if (mbdType === "anyAmount" || mbd <= 0 || referencePrice === null) return null;
    if (isReverse) {
      if (mbdType === "atLeastMbd")   return `📌 Current lowest: ${currency} ${fmt(referencePrice)} — Max allowed: ${currency} ${fmt(referencePrice - mbd)}`;
      if (mbdType === "multiplesMbd") return `📌 Current lowest: ${currency} ${fmt(referencePrice)} — Valid bids: ${currency} ${fmt(referencePrice - mbd)}, ${currency} ${fmt(referencePrice - mbd * 2)}...`;
    } else if (isForward) {
      if (mbdType === "atLeastMbd")   return `📌 Current highest: ${currency} ${fmt(referencePrice)} — Min bid: ${currency} ${fmt(referencePrice + mbd)}`;
      if (mbdType === "multiplesMbd") return `📌 Current highest: ${currency} ${fmt(referencePrice)} — Valid bids: ${currency} ${fmt(referencePrice + mbd)}, ${currency} ${fmt(referencePrice + mbd * 2)}...`;
    }
    return null;
  };
  //eslint-disable-next-line
  const mbdHint = getMbdHint();

  const headerTitle = isBuyer
    ? `🏷️ Bid Monitor${bidConfigType === "serial" ? " (Serial Lots)" : bidConfigType === "staggered" ? " (Staggered)" : ""}`
    : auctionIsActuallyEnded
      ? "Auction Ended"
      : isInitialQuoteMode
        ? "📋 Submit Your Initial Quote"
        : isForward
          ? "🔨 Place Bid (Higher Bids Win)"
          : "🔨 Place Bid (Lower Bids Win)";

  const footerText = isInitialQuoteMode
    ? "* Enter your initial price for each item. After submitting, normal bidding rules apply."
    : isForward
      ? "* Higher bids win. Each item submitted independently."
      : "* Lower bids win. Each item submitted independently.";

  return (
    <div className="accordion mb-4" id="accordionBidSubmit">
      <div className="accordion-item" style={{ border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "hidden" }}>

        {/* ── Header ────────────────────────────────────────────────────── */}
        <h2 className="accordion-header">
          <Row className="accordion-button" style={{ background: "#fff", margin: 0 }}>
            <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
              <i
                className={`bi ${isCardVisible ? "bi-chevron-right" : "bi-chevron-down"}`}
                onClick={() => setIsCardVisible(!isCardVisible)}
                // style={{ cursor: "pointer", color: "#6b7280", fontSize: "14px" }}
              />
            </Col>
            <Col xs={1} md={1} lg={1} className="text-center logoBtn">
              <span className="iconLogo" style={{ fontSize: "18px" }}>
                {isBuyer ? "🏷️" : isInitialQuoteMode ? "📋" : isForward ? "📈" : "📉"}
              </span>
            </Col>
            <Col xs={4} md={5} lg={5}>
              <span className="headerTitle" style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{headerTitle}</span>
              {!isBuyer && !auctionIsActuallyEnded && !isInitialQuoteMode && (
                <span style={{ display: "block", fontSize: "11px", fontWeight: 500, color: "#6b7280", marginTop: "2px" }}>
                  {bidAgainst === "ownPrice"
                    ? "Your next bid must beat your own last bid by MBD"
                    : "Your next bid must beat the current L1 price by MBD"
                  }
                </span>
              )}
              <span className="subHeaderTitle" style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "12px", color: isConnected ? "#059669" : "#ef4444", marginTop: "2px" }}>
                <span style={{
                  width: "7px", height: "7px", borderRadius: "50%",
                  background: isConnected ? "#10b981" : "#f87171",
                  display: "inline-block",
                  boxShadow: isConnected ? "0 0 5px #10b981" : "none",
                  animation: isConnected ? "pulse 1.5s infinite" : "none",
                }} />
                {isConnected ? (isBuyer ? "Monitoring Live" : "Live") : "Disconnected"}
              </span>
            </Col>
            <Col xs={5} md={4} lg={4} className="headerButtonAlign" style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" }}>

              {/* Initial Quote badge */}
              {isInitialQuoteMode && (
                <span className="pill pill-indigo">📋 Initial Quote</span>
              )}

              {/* bidAgainst badge */}
              {!isInitialQuoteMode && (
                <span className={`pill ${bidAgainst === "ownPrice" ? "pill-purple" : "pill-blue"}`}>
                  {bidAgainst === "ownPrice" ? "👤 Own Price" : "🏆 Leading Price"}
                </span>
              )}

              {bidConfigType !== "parallel" && (
                <span className="pill pill-blue">
                  {bidConfigType === "serial" ? "🔢 Serial" : "⏱️ Staggered"}
                </span>
              )}
              {activeLot && (
                <span className="pill pill-green">🟢 Lot {activeLot} Active</span>
              )}
              <span className={`pill ${auctionIsActuallyEnded ? "pill-red" : auctionStatus === "extended" ? "pill-amber" : "pill-green"}`}>
                {auctionIsActuallyEnded ? "🔴 Ended" : "🟢 Live"}
              </span>
            </Col>
          </Row>
        </h2>

        <div className={`accordion-collapse collapse show ${isCardVisible ? "visible" : ""}`}>
          <div className="accordion-body" style={{ padding: 0, background: "#fff" }}>

            {/* Buyer monitor banner */}
            {isBuyer && (
              <div className="bsp-banner banner-slate" style={{ fontSize: "13px" }}>
                <i className="bi bi-info-circle" style={{ marginTop: "1px" }}></i>
                <span>
                  Buyer Monitor View — Bid submission disabled.
                  {activeLot && (
                    <span className="pill pill-gray" style={{ marginLeft: "10px" }}>Lot {activeLot} active</span>
                  )}
                </span>
              </div>
            )}

            {/* Initial Quote Mode banner */}
            {isInitialQuoteMode && (
              <div className="bsp-banner banner-indigo" style={{ borderLeft: "4px solid #6366f1", paddingLeft: "14px" }}>
                <span style={{ fontSize: "22px", lineHeight: 1, marginTop: "1px" }}>📋</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "14px", marginBottom: "4px", color: "#3730a3" }}>
                    Initial Quote Submission
                  </div>
                  <div style={{ fontWeight: 400, fontSize: "13px", color: "#4338ca", lineHeight: 1.6 }}>
                    Please enter your initial price for each item below and click <strong>Submit Quote</strong>.
                    Once submitted, these prices will be used to rank all suppliers.
                    After all quotes are in, normal bidding will begin with{" "}
                    <strong>{bidAgainst === "ownPrice" ? "Own Price" : "Leading Price"}</strong> rules applied.
                  </div>
                </div>
              </div>
            )}

            {/* Serial / Staggered info banner */}
            {bidConfigType !== "parallel" && !isEnded && !isBuyer && (
              <div className="bsp-banner banner-blue" style={{ fontSize: "13px" }}>
                {bidConfigType === "serial"
                  ? <><span>🔢</span><span><strong>Serial Bidding:</strong> Each item has its own Submit button. Only active lot items can be submitted.</span></>
                  : <><span>⏱️</span><span><strong>Staggered Bidding:</strong> Each item closes at a different time. Submit before it closes!</span></>
                }
              </div>
            )}

            {/* Bid accepted success banner */}
            {lastBidStatus?.status === "accepted" && !isBuyer && (
              <div className="bsp-banner banner-green" style={{ fontSize: "13px" }}>
                <span>✅</span><span>{lastBidStatus.message}</span>
              </div>
            )}

            {/* Buyer firstQuote info */}
            {isBuyer && firstQuoteBy === "Buyer" && (
              <div className="bsp-banner banner-slate" style={{ fontSize: "13px" }}>
                <span>📌</span>
                <span><strong>Initial Rankings:</strong> Based on buyer-entered prices. These will update as suppliers bid.</span>
              </div>
            )}

            {/* ── Main content ─────────────────────────────────────────── */}
            <div style={{ padding: "18px" }}>
              {auctionItems.length === 0 ? (
                <div style={{ textAlign: "center", color: "#9ca3af", padding: "32px", fontSize: "14px" }}>
                  No items to bid on.
                </div>
              ) : (
                <>
                  {/* ── LOT TIMING TABLE ─────────────────────────────── */}
                  {bidConfigType !== "parallel" && lotTimingRows.length > 0 && (
                    <div className="bsp-card">
                      <div
                        className="bsp-card-head"
                        onClick={() => setShowLotTimings(!showLotTimings)}
                      >
                        <span className="bsp-card-head-title">
                          {bidConfigType === "staggered" ? "⏱️ Item Close Times" : "⏱️ Lot Bid Windows"}
                          <span className="pill pill-blue" style={{ fontSize: "11px", padding: "2px 9px" }}>
                            {lotTimingRows.length}
                          </span>
                        </span>
                        <i className={`bi bi-chevron-${showLotTimings ? "up" : "down"}`} style={{ fontSize: "13px", color: "#6b7280" }} />
                      </div>
                      {showLotTimings && (
                        <div style={{ overflowX: "auto" }}>
                          <table className="bsp-dt">
                            <thead>
                              <tr>
                                <th>Lot / Item</th>
                                <th>Items</th>
                                <th>Start</th>
                                <th>End</th>
                                <th className="c">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lotTimingRows.map((lot, idx) => {
                                const now      = new Date();
                                const isActive = lot.startTime && lot.endTime && now >= new Date(lot.startTime) && now < new Date(lot.endTime);
                                const isClosed = lot.endTime && now >= new Date(lot.endTime);
                                const rowClass = isActive ? "row-active" : isClosed ? "row-closed" : "row-pending";
                                return (
                                  <tr key={idx} className={rowClass} style={{ background: isActive ? "#f9fffe" : isClosed ? "#fff9f9" : "#fff" }}>
                                    <td style={{ fontWeight: 800, color: "#0f766e" }}>
                                      {bidConfigType === "serial" ? `Lot ${lot.lotNumber}` : lot.lotNumber}
                                    </td>
                                    <td>
                                      <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                                        {lot.items.map((item, i) => (
                                          <span key={i} className="pill pill-blue" style={{ fontSize: "12px", padding: "3px 10px" }}>
                                            {item.itemCode || item.itemNo || `Item ${item.local_id}`}
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                    <td style={{ color: "#374151" }}>{formatDateTime(lot.startTime)}</td>
                                    <td style={{ color: isActive ? "#dc2626" : "#374151", fontWeight: isActive ? 700 : 400 }}>
                                      {formatDateTime(lot.endTime)}
                                    </td>
                                    <td className="c">
                                      <span className={`pill ${isActive ? "pill-green" : isClosed ? "pill-red" : "pill-slate"}`}>
                                        {isActive ? "🟢 Live" : isClosed ? "🔴 Closed" : "⏳ Upcoming"}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── SUPPLIER BID TABLE ───────────────────────────── */}
                  {!isBuyer && (
                    <div style={{ marginBottom: "20px" }}>
                      {/* Bid table */}
                      <div className="bsp-card" style={{ marginBottom: 0 }}>
                        <div style={{ overflowX: "auto" }}>
                          <table className="bsp-dt">
                            <thead>
                              <tr>
                                <th>Item Code</th>
                                <th>Item Name</th>
                                <th className="r">Qty</th>
                                {startPrice && totalStartPrice > 0 && (
                                  <th className="r">Start Price</th>
                                )}
                                {mbdType !== "anyAmount" && (
                                  <th className="r">MBD</th>
                                )}
                                <th className="c">
                                  {isInitialQuoteMode ? "Quote Status" : "Your Rank"}
                                </th>
                                {bidAgainst === "leadingPrice" && !isInitialQuoteMode && (
                                  <th className="r">L1 Price</th>
                                )}
                                {!isInitialQuoteMode && (
                                  <th className="r">Your Last Bid</th>
                                )}
                                <th>
                                  {isInitialQuoteMode ? "Your Quote" : "🔨 Place Bid"}
                                </th>
                                <th className="c">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {auctionItems.map((item, idx) => {
                                const key = (item.local_id !== undefined && item.local_id !== null)
                                  ? String(item.local_id)
                                  : (item.itemCode !== undefined && item.itemCode !== null)
                                    ? String(item.itemCode)
                                    : String(item._id || idx);

                                const lotStatus    = getLotStatus(key);
                                // eslint-disable-next-line
                                const lotMeta      = getLotStatusMeta(lotStatus);
                                const isActive     = lotStatus === "active";
                                const itemRankings = getItemRankings(key);
                                const bestBid      = itemRankings.length > 0 ? itemRankings[0].bidAmount : null;
                                const myItemRank   = itemRankings.find(r => String(r.supplierCode) === String(supplierCode));
                                // eslint-disable-next-line
                                const ownLastBid   = getOwnLastBidForItem(key);
                                const submitting   = isSubmitting[key];
                                const lotNo        = bidTimings?.[key]?.lotNumber;
                                const itemEndTime  = bidTimings?.[key]?.endTime;
                                 // eslint-disable-next-line
                                const secsToClose  = itemEndTime
                                  ? Math.max(0, Math.floor((new Date(itemEndTime) - new Date()) / 1000))
                                  : null;

                                const thisItemQuoted        = firstQuote && firstQuoteBy === "Supplier" && supplierHasQuoteForItem(key);
                                const showAsInitialQuoteItem = firstQuote && firstQuoteBy === "Supplier" && !thisItemQuoted;

                                // Row class
                                const rowClass = showAsInitialQuoteItem
                                  ? "row-quote"
                                  : isActive ? "row-active"
                                  : auctionIsActuallyEnded ? "row-ended"
                                  : lotStatus === "closed" ? "row-closed"
                                  : "row-pending";

                                // Button label
                                const btnLabel = submitting
                                  ? "⏳..."
                                  : lotStatus === "closed"
                                    ? "🔴 Closed"
                                    : lotStatus === "pending" && !showAsInitialQuoteItem
                                      ? "⏳ Pending"
                                      : showAsInitialQuoteItem
                                        ? "📋 Submit Quote"
                                        : `${isForward ? "📈" : "📉"} Submit`;

                                const btnDisabled = !canBid || submitting || !bidAmounts[key] ||
                                  (!showAsInitialQuoteItem && bidConfigType !== "parallel" && !isActive);
                                const btnClass = btnDisabled
                                  ? "bsp-btn btn-closed"
                                  : showAsInitialQuoteItem
                                    ? "bsp-btn btn-indigo"
                                    : isForward
                                      ? "bsp-btn btn-blue"
                                      : "bsp-btn btn-teal";

                                const inputClass = `bsp-input ${showAsInitialQuoteItem ? "input-quote" : "input-active"}`;

                                const sp = getItemStartPrice(item);

                                return (
                                  <tr key={key} className={rowClass} style={{ background: "#fff" }}>

                                    {/* Item Code */}
                                    <td style={{ fontWeight: 700, fontSize: "14px", color: "#111827" }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        {lotNo && bidConfigType === "serial" && (
                                          <span className={`pill ${isActive ? "pill-teal" : "pill-slate"}`} style={{ fontSize: "10px", padding: "1px 6px" }}>
                                            Lot {lotNo}
                                          </span>
                                        )}
                                        {item.itemCode || item.itemNo || key}
                                      </div>
                                    </td>

                                    {/* Item Name */}
                                    <td style={{ fontSize: "13px", color: "#6b7280", minWidth: "120px" }}>
                                      {item.itemDescription || "--"}
                                    </td>

                                    {/* Qty */}
                                    <td className="r" style={{ fontSize: "13px", color: "#374151" }}>
                                      {item.quantity || 1} {item.uom || ""}
                                    </td>

                                    {/* Start Price */}
                                    {startPrice && totalStartPrice > 0 && (
                                      <td className="r" style={{ color: "#1d4ed8", fontWeight: 700, fontSize: "14px" }}>
                                        {sp ? `${currency} ${fmt(sp)}` : "--"}
                                      </td>
                                    )}

                                    {/* MBD */}
                                    {mbdType !== "anyAmount" && (() => {
                                      const perItemMbd = parseFloat(itemMbd?.[key]) || mbd;
                                      const mbdLabel = mbdType === "atLeastMbd"
                                        ? `Min ${currency} ${fmt(perItemMbd)}`
                                        : mbdType === "multiplesMbd"
                                          ? `Multiple of ${currency} ${fmt(perItemMbd)}`
                                          : `${currency} ${fmt(perItemMbd)}`;
                                      return (
                                        <td className="r" style={{ color: "#b45309", fontWeight: 600, fontSize: "12px" }}>
                                          {mbdLabel}
                                        </td>
                                      );
                                    })()}

                                    {/* Quote Status / Rank */}
                                    <td className="c">
                                      {showAsInitialQuoteItem ? (
                                        <span className="pill pill-slate">⏳ Pending Quote</span>
                                      ) : myItemRank ? (
                                        <span className="pill pill-gray" style={{ padding: "4px 12px", borderRadius: "20px" }}>
                                          {myItemRank.rank === 1 ? "🥇 L1" : `🏅 L${myItemRank.rank}`}
                                        </span>
                                      ) : (
                                        <span style={{ fontSize: "13px", color: "#9ca3af", fontStyle: "italic" }}>No bid</span>
                                      )}
                                    </td>

                                    {/* L1 Price - Only show in Leading Price mode */}
                                    {bidAgainst === "leadingPrice" && !isInitialQuoteMode && (
                                      <td className="r" style={{ fontWeight: 700, fontSize: "14px", color: "#059669" }}>
                                        {bestBid != null ? `${currency} ${fmt(bestBid)}` : "--"}
                                      </td>
                                    )}

                                    {/* Your Last Bid - Show in both modes */}
                                    {!isInitialQuoteMode && (
                                      <td className="r" style={{ fontWeight: 700, fontSize: "14px", color: "#7c3aed" }}>
                                        {ownLastBid != null ? `${currency} ${fmt(ownLastBid)}` : "--"}
                                      </td>
                                    )}

                                    {/* Bid input */}
                                    <td>
                                      {auctionIsActuallyEnded ? (
                                        <span style={{ fontSize: "13px", color: "#9ca3af", fontStyle: "italic" }}>—</span>
                                      ) : (
                                        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                          <input
                                            type="number"
                                            min="0"
                                            step={Math.pow(10, -decimals)}
                                            value={bidAmounts[key] || ""}
                                            onChange={e => handleAmountChange(key, e.target.value)}
                                            disabled={!canBid || submitting || (!showAsInitialQuoteItem && bidConfigType !== "parallel" && !isActive)}
                                            placeholder={
                                              !canBid ? "Not connected..."
                                                : showAsInitialQuoteItem ? "Enter price..."
                                                : bidConfigType !== "parallel" && !isActive
                                                  ? lotStatus === "closed" ? "Closed" : "Pending..."
                                                  : isForward ? "Higher..." : "Lower..."
                                            }
                                            className={inputClass}
                                          />
                                          <span style={{ fontSize: "12px", color: "#6b7280" }}>{currency}</span>
                                        </div>
                                      )}
                                    </td>

                                    {/* Action */}
                                    <td className="c">
                                      {auctionIsActuallyEnded ? (
                                        <span style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic" }}>—</span>
                                      ) : thisItemQuoted && !myItemRank && !bestBid ? (
                                        <span style={{ fontSize: "12px", color: "#059669", fontWeight: 700 }}>✅ Submitted</span>
                                      ) : (
                                        <button
                                          onClick={() => handleSingleItemSubmit(key, item)}
                                          disabled={btnDisabled}
                                          className={btnClass}
                                        >
                                          {btnLabel}
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── BUYER VIEW TABLE ─────────────────────────────── */}
                  {isBuyer && (
                    <div className="bsp-card" style={{ marginBottom: "16px" }}>
                      <div style={{ overflowX: "auto" }}>
                        <table className="bsp-dt">
                          <thead>
                            <tr>
                              <th style={{ width: "40px" }}>#</th>
                              <th>Item</th>
                              <th>Description</th>
                              <th className="r">Qty</th>
                              {startPrice && <th className="r" style={{ color: "#1d4ed8" }}>Start Price</th>}
                              {bidConfigType !== "parallel" && <th className="c">Status</th>}
                              {bidConfigType === "serial" && <th className="c">Lot</th>}
                              {bidConfigType === "staggered" && <th className="c" style={{ color: "#dc2626" }}>Closes At</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {auctionItems.map((item, idx) => {
                              const key       = item.local_id ?? item.itemCode ?? String(item._id || idx);
                              const sp        = getItemStartPrice(item);
                              const lotNo     = bidTimings?.[key]?.lotNumber;
                              const lotStatus = getLotStatus(key);
                              const lotMeta   = getLotStatusMeta(lotStatus);
                              const isActive  = lotStatus === "active";
                              const rowClass  = "";
                              return (
                                <tr key={key} className={rowClass} style={{ background: "#fff" }}>
                                  <td style={{ color: "#9ca3af" }}>{idx + 1}</td>
                                  <td style={{ fontWeight: 700, color: "#111827" }}>{item.itemCode || item.itemNo || key}</td>
                                  <td style={{ color: "#6b7280" }}>{item.itemDescription || "--"}</td>
                                  <td className="r" style={{ color: "#374151" }}>{item.quantity || 1} {item.uom || ""}</td>
                                  {startPrice && (
                                    <td className="r" style={{ color: "#1d4ed8", fontWeight: 700 }}>
                                      {sp ? fmt(sp) : "--"}
                                    </td>
                                  )}
                                  {bidConfigType !== "parallel" && (
                                    <td className="c">
                                      <span style={{ background: lotMeta.bg, border: `1px solid ${lotMeta.border}`, color: lotMeta.color, borderRadius: "10px", padding: "3px 12px", fontSize: "12px", fontWeight: 700, display: "inline-block" }}>
                                        {lotMeta.label}
                                      </span>
                                    </td>
                                  )}
                                  {bidConfigType === "serial" && (
                                    <td className="c">
                                      {lotNo
                                        ? <span style={{ fontWeight: 700, color: "#374151" }}>Lot {lotNo}</span>
                                        : "--"
                                      }
                                    </td>
                                  )}
                                  {bidConfigType === "staggered" && (
                                    <td className="c">
                                      <span style={{ fontWeight: 700, fontSize: "13px", color: isActive ? "#dc2626" : "#9ca3af" }}>
                                        {bidTimings?.[key]?.endTime
                                          ? new Date(bidTimings[key].endTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })
                                          : "--"}
                                      </span>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── ITEM-WISE RANKINGS ───────────────────────────── */}
                  {auctionItems.length > 0 && (
                    <div className="bsp-card" style={{ marginTop: "4px" }}>
                      <div
                        className="bsp-card-head"
                        onClick={() => setShowItemRanking(!showItemRanking)}
                      >
                        <span className="bsp-card-head-title" style={{ color: "#374151" }}>
                          Item-wise Live Rankings
                          <span className="pill pill-gray" style={{ fontSize: "11px", padding: "2px 9px" }}>
                            {auctionItems.length} items
                          </span>
                          {isInitialQuoteMode && (
                            <span className="pill pill-indigo" style={{ fontSize: "11px", padding: "2px 9px" }}>
                              Initial Quote Phase
                            </span>
                          )}
                        </span>
                        <i className={`bi bi-chevron-${showItemRanking ? "up" : "down"}`} style={{ fontSize: "13px", color: "#6b7280" }} />
                      </div>

                      {showItemRanking && (
                        <div style={{ maxHeight: "360px", overflowY: "auto" }}>
                          {auctionItems.map((item, iIdx) => {
                            const key           = item.local_id ?? item.itemCode ?? String(item._id || iIdx);
                            const itemRankings  = getItemRankings(key);
                            const topRankings   = itemRankings.slice(0, 3);
                            const myItemRank    = !isBuyer ? itemRankings.find(r => r.supplierCode === supplierCode) : null;
                            const hasInitialPrices = topRankings.length > 0 && topRankings[0]?.isInitialPrice;
                            const thisItemQuotedForRankings = firstQuote && firstQuoteBy === "Supplier" && supplierHasQuoteForItem(key);

                            return (
                              <div key={key} style={{ borderBottom: iIdx < auctionItems.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                                {/* Item header row */}
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 18px", background: "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                                  <div style={{ fontWeight: 600, fontSize: "13.5px", color: "#374151", display: "flex", alignItems: "center", gap: "8px" }}>
                                    {bidTimings?.[key]?.lotNumber && (
                                      <span className="pill pill-gray" style={{ fontSize: "11px", padding: "2px 8px" }}>
                                        Lot {bidTimings[key].lotNumber}
                                      </span>
                                    )}
                                    <span style={{ fontWeight: 700, color: "#111827" }}>{item.itemCode || item.itemNo || key}</span>
                                    {item.itemDescription && (
                                      <span style={{ fontWeight: 400, color: "#6b7280", fontSize: "12px" }}>— {item.itemDescription}</span>
                                    )}
                                    {isBuyer && (
                                      <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                                        ({topRankings.length} {hasInitialPrices ? "initial prices" : "bidder" + (topRankings.length !== 1 ? "s" : "")})
                                      </span>
                                    )}
                                  </div>
                                  {!isBuyer && firstQuote && firstQuoteBy === "Supplier" && (
                                    <span className={`pill ${thisItemQuotedForRankings ? "pill-green" : "pill-slate"}`}>
                                      {thisItemQuotedForRankings ? "✅ Quoted" : "⏳ Not Quoted"}
                                    </span>
                                  )}
                                  {myItemRank && !firstQuote && (
                                    <span className="pill pill-gray">
                                      {myItemRank.rank === 1 ? "🥇 You're L1" : `🏅 You: L${myItemRank.rank}`}
                                    </span>
                                  )}
                                </div>

                                {/* Buyer ranking table */}
                                {isBuyer ? (
                                  <div style={{ background: "#fff", paddingBottom: "8px" }}>
                                    {topRankings.length === 0 ? (
                                      <div style={{ padding: "14px 18px", fontSize: "13px", color: "#9ca3af", fontStyle: "italic" }}>No bids yet.</div>
                                    ) : (
                                      <table className="bsp-dt">
                                        <thead>
                                          <tr>
                                            <th>Rank</th>
                                            <th>Bid Amount</th>
                                            <th>Time</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {topRankings.map((r, rIdx) => {
                                            const rm = getRankBadge(r.rank);
                                            return (
                                              <tr key={rIdx} style={{ background: "#fff" }}>
                                                <td>
                                                  <span style={{ background: rm.bg, color: rm.color, border: `1px solid ${rm.border}`, borderRadius: "6px", padding: "3px 12px", fontSize: "12px", fontWeight: 700, display: "inline-block" }}>
                                                    {rm.emoji} L{r.rank}
                                                  </span>
                                                </td>
                                                <td style={{ fontWeight: 700, color: r.rank === 1 ? "#059669" : "#111827", fontSize: "14px" }}>
                                                  {currency} {fmt(r.bidAmount)}
                                                </td>
                                                <td style={{ color: "#9ca3af", fontSize: "13px" }}>
                                                  {r.bidTime ? formatTime(r.bidTime) : "Buyer set"}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    )}
                                    {topRankings.length < itemRankings.length && (
                                      <div style={{ padding: "5px 18px", fontSize: "12px", color: "#6b7280" }}>
                                        +{itemRankings.length - topRankings.length} more bidder(s)
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  /* Supplier ranking row */
                                  <div style={{ padding: "14px 18px", background: "#fff" }}>
                                    {myItemRank ? (
                                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                          <span className={`pill ${myItemRank.rank === 1 ? "pill-green" : "pill-slate"}`} style={{ padding: "4px 12px" }}>
                                            {myItemRank.rank === 1 ? "🥇 L1" : `🏅 L${myItemRank.rank}`}
                                          </span>
                                          <span style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
                                            {isInitialQuoteMode || (firstQuote && firstQuoteBy === "Supplier")
                                              ? "Your Initial Quote"
                                              : auctionIsActuallyEnded ? "Your Final Bid" : "Your Bid"}
                                          </span>
                                        </div>
                                        <span style={{ fontSize: "15px", fontWeight: 800, color: myItemRank.rank === 1 ? "#059669" : "#374151" }}>
                                          {currency} {typeof myItemRank.bidAmount === "number"
                                            ? myItemRank.bidAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })
                                            : myItemRank.bidAmount}
                                        </span>
                                      </div>
                                    ) : (
                                      <span style={{ fontSize: "13px", color: "#9ca3af", fontStyle: "italic" }}>
                                        {auctionIsActuallyEnded
                                          ? "No bid placed for this item."
                                          : firstQuote && firstQuoteBy === "Supplier"
                                            ? "Submit your initial quote for this item above."
                                            : "You haven't placed a bid for this item yet."}
                                      </span>
                                    )}
                                    {!auctionIsActuallyEnded && myItemRank?.rank === 1 && (
                                      <div className="bsp-banner banner-green" style={{ marginTop: "10px", borderRadius: "6px", borderBottom: "none", fontSize: "13px", fontWeight: 600, justifyContent: "center" }}>
                                        🎉 {firstQuote && firstQuoteBy === "Supplier"
                                          ? "You have the best initial quote for this item!"
                                          : "You are the L1 bidder for this item!"}
                                      </div>
                                    )}
                                    {auctionIsActuallyEnded && (
                                      <div style={{ marginTop: "10px", padding: "8px 14px", background: "#f9fafb", borderRadius: "6px", fontSize: "13px", color: "#6b7280", textAlign: "center", border: "1px solid #e5e7eb" }}>
                                        The auction has ended. Bidding is no longer available.
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Footer text ──────────────────────────────────── */}
                  {!isBuyer && (
                    <div style={{ fontSize: "12.5px", color: "#6b7280", textAlign: "center", marginTop: "10px" }}>
                      {footerText}
                    </div>
                  )}

                  {/* ── Buyer summary bar ────────────────────────────── */}
                  {isBuyer && (
                    <div style={{ padding: "12px 18px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "12px", color: "#6b7280", marginTop: "14px", display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "center" }}>
                      <span>Items: <strong style={{ color: "#374151" }}>{auctionItems.length}</strong></span>
                      <span>Bidders: <strong style={{ color: "#374151" }}>{liveBidRanking.length}</strong></span>
                      {liveBidRanking[0] && (
                        <span>L1 Bid: <strong style={{ color: "#059669" }}>{currency} {fmt(liveBidRanking[0].totalBid)}</strong></span>
                      )}
                      {bidConfigType === "serial" && activeLot && (
                        <span>Active Lot: <strong style={{ color: "#374151" }}>Lot {activeLot}</strong></span>
                      )}
                      {firstQuote && firstQuoteBy === "Supplier" && (
                        <span>Quote Mode: <strong style={{ color: "#374151" }}>Supplier Initial Quotes</strong></span>
                      )}
                      <span>Bid Mode: <strong style={{ color: "#374151" }}>
                        {bidAgainst === "ownPrice" ? "Own Price" : "Leading Price"}
                      </strong></span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Inject styles */}
      <style>{COMPONENT_STYLES}</style>

      {/* Bid Confirmation Modal */}
      <BidSubmitConfirmationModal
        show={bidConfirmModal.show}
        onHide={() => setBidConfirmModal({ show: false, itemKey: null, item: null, amount: 0 })}
        onConfirm={handleConfirmBid}
        bidAmount={bidConfirmModal.amount}
        currency={currency}
        itemCode={bidConfirmModal.item?.itemCode || bidConfirmModal.item?.itemNo || bidConfirmModal.itemKey || ""}
        itemDescription={bidConfirmModal.item?.itemDescription || ""}
        quantity={bidConfirmModal.item?.quantity || 1}
        uom={bidConfirmModal.item?.uom || ""}
      />
    </div>
  );
};

export default BidSubmitPanel;
